import React from 'react';

const VARIANTS = {
  primary: 'w-full max-w-[24rem] inline-flex items-center justify-center rounded-lg bg-theme-primary text-theme-neutral hover:bg-theme-primary/80 h-[46px] text-sm font-semibold shadow-sm focus:outline-none',
  secondary: 'w-full max-w-[24rem] px-5 rounded-lg bg-white border-2 border-theme-primary text-theme-primary hover:bg-theme-primary/10 font-semibold text-sm h-[46px] shadow-inner transition',
};

// simple class combiner
function cn(...args) {
  return args.filter(Boolean).join(' ');
}

const Button = React.forwardRef(({
  variant = 'primary',
  className = '',
  children,
  prefix,        // React node for custom icon component
  prefixSrc,     // image src string
  prefixAlt = '',
  prefixClassName = 'h-4 w-4',
  isLoading = false,
  disabled = false,
  type = 'button',
  ...props
}, ref) => {
  const isDisabled = disabled || isLoading;
  const variantClass = VARIANTS[variant] || '';
  const classes = cn(
    variantClass,
    className,
    isDisabled && 'opacity-60 cursor-not-allowed'
  );

  // build prefix node only when provided
  let prefixNode = null;
  if (prefix) {
    prefixNode = <span className={cn('inline-flex items-center', prefixClassName)}>{prefix}</span>;
  } else if (prefixSrc) {
    prefixNode = (
      <img src={prefixSrc} alt={prefixAlt} className={cn('inline-block', prefixClassName)} />
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {/* show loading spinner if loading, otherwise content. keep prefix visible while loading optionally */}
      {isLoading ? (
        <span className="inline-flex items-center gap-2">
          <svg
            className="animate-spin h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <span>{children}</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-2">
          {prefixNode}
          <span>{children}</span>
        </span>
      )}
    </button>
  );
});

export const buttonVariants = VARIANTS;
export { Button };

export default Button;
